import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, type UsersListParams } from '../api/user.api';
import type { UserCreateInput, UserUpdateInput, InviteInput } from '@cms/models';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: userApi.getAll,
  });
}

export function useUsersPaginated(params: UsersListParams = {}) {
  const { page = 1, pageSize = 25, search, status, sortBy, sortOrder, platformUsersOnly } =
    params;
  return useQuery({
    queryKey: [
      'users',
      'paginated',
      page,
      pageSize,
      search ?? '',
      status ?? '',
      sortBy ?? '',
      sortOrder ?? '',
      platformUsersOnly ? 1 : 0,
    ],
    queryFn: () =>
      userApi.getPaginated({
        page,
        pageSize,
        search: search || undefined,
        status,
        sortBy,
        sortOrder,
        platformUsersOnly,
      }),
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => userApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreateInput) => userApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateInput }) => userApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteInput) => userApi.invite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
